import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { RaceTransferRule } from './RaceTransferRule';
import { ResultSetupRace } from './ResultSetupRace';
import type { EntityState } from './../enums/EntityState';
import type { RoundType } from './../enums/RoundType';
import type { VirtualRaceType } from './../enums/VirtualRaceType';
import { formatDateIso } from '@/utilities/dateutils';

export class Race extends BaseObj {
    public ID: number;
    public __id: number;

    public CancellationDeadline: Date | null;
    public __cancellationdeadline: Date | null;

    public Date: Date | null;
    public __date: Date | null;

    public DecimalPlacesInResult: number | null;
    public __decimalplacesinresult: number | null;

    public DigitsInBib: number | null;
    public __digitsinbib: number | null;

    public DisciplineID: number | null;
    public __disciplineid: number | null;

    public Distance: number;
    public __distance: number;

    public EventID: number;
    public __eventid: number;

    public HideFromLive: boolean;
    public __hidefromlive: boolean;

    public IndividualStart: boolean;
    public __individualstart: boolean;

    public Level: number;
    public __level: number;

    public MapID: number | null;
    public __mapid: number | null;

    public MaxContestants: number | null;
    public __maxcontestants: number | null;

    public Name: string | null;
    public __name: string | null;

    public Number: number;
    public __number: number;

    public OverrideSpeed: number | null;
    public __overridespeed: number | null;

    public Pursuit: boolean;
    public __pursuit: boolean;

    public RaceTransferRules: RaceTransferRule[];
    public __racetransferrules: RaceTransferRule[];

    public RankingResolution: number | null;
    public __rankingresolution: number | null;

    public RelayAllowTeamnameChange: boolean;
    public __relayallowteamnamechange: boolean;

    public RelayDuplicateParticipant: boolean;
    public __relayduplicateparticipant: boolean;

    public RelayGetParticipantInfo: boolean;
    public __relaygetparticipantinfo: boolean;

    public RelayHideChip: boolean;
    public __relayhidechip: boolean;

    public RelayHideSearch: boolean;
    public __relayhidesearch: boolean;

    public RelayTeamMaxContestants: number | null;
    public __relayteammaxcontestants: number | null;

    public RelayTeamMinContestants: number | null;
    public __relayteammincontestants: number | null;

    public RequireActiveLicense: boolean;
    public __requireactivelicense: boolean;

    public ResultParent: number | null;
    public __resultparent: number | null;

    public readonly ResultSetup: ResultSetupRace | null;

    public RoundType: RoundType | null;
    public __roundtype: RoundType | null;

    public Run: number;
    public __run: number;

    public ShortName: string | null;
    public __shortname: string | null;

    public SportsLevelID: number | null;
    public __sportslevelid: number | null;

    public TrackColorLive: string | null;
    public __trackcolorlive: string | null;

    public VirtualRaceExternalURL: string | null;
    public __virtualraceexternalurl: string | null;

    public VirtualRaceType: VirtualRaceType;
    public __virtualracetype: VirtualRaceType;

    public VirtualTimeType: number | null;
    public __virtualtimetype: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        CancellationDeadline?: Date | null;
        Date?: Date | null;
        DecimalPlacesInResult?: number | null;
        DigitsInBib?: number | null;
        DisciplineID?: number | null;
        Distance?: number;
        EntityState?: EntityState;
        EventID?: number;
        HideFromLive?: boolean;
        IndividualStart?: boolean;
        Level?: number;
        MapID?: number | null;
        MaxContestants?: number | null;
        Name?: string | null;
        Number?: number;
        OverrideSpeed?: number | null;
        Pursuit?: boolean;
        RaceTransferRules?: RaceTransferRule[];
        RankingResolution?: number | null;
        RelayAllowTeamnameChange?: boolean;
        RelayDuplicateParticipant?: boolean;
        RelayGetParticipantInfo?: boolean;
        RelayHideChip?: boolean;
        RelayHideSearch?: boolean;
        RelayTeamMaxContestants?: number | null;
        RelayTeamMinContestants?: number | null;
        RequireActiveLicense?: boolean;
        ResultParent?: number | null;
        ResultSetup?: ResultSetupRace | null;
        RoundType?: RoundType | null;
        Run?: number;
        ShortName?: string | null;
        SportsLevelID?: number | null;
        TrackColorLive?: string | null;
        VirtualRaceExternalURL?: string | null;
        VirtualRaceType?: VirtualRaceType;
        VirtualTimeType?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Race>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Race tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CancellationDeadline = values.CancellationDeadline ?? null;
        this.__cancellationdeadline = values.CancellationDeadline ?? null;
        Object.defineProperty(this, 'CancellationDeadline', {
            get(): Date | null {
                return this.__cancellationdeadline;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('CancellationDeadline', this.__cancellationdeadline, value);
                this.__cancellationdeadline = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Date = values.Date ?? null;
        this.__date = values.Date ?? null;
        Object.defineProperty(this, 'Date', {
            get(): Date | null {
                return this.__date;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Date', this.__date, value);
                this.__date = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DecimalPlacesInResult = values.DecimalPlacesInResult ?? null;
        this.__decimalplacesinresult = values.DecimalPlacesInResult ?? null;
        Object.defineProperty(this, 'DecimalPlacesInResult', {
            get(): number | null {
                return this.__decimalplacesinresult;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DecimalPlacesInResult', this.__decimalplacesinresult, value);
                this.__decimalplacesinresult = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DigitsInBib = values.DigitsInBib ?? null;
        this.__digitsinbib = values.DigitsInBib ?? null;
        Object.defineProperty(this, 'DigitsInBib', {
            get(): number | null {
                return this.__digitsinbib;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DigitsInBib', this.__digitsinbib, value);
                this.__digitsinbib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisciplineID = values.DisciplineID ?? null;
        this.__disciplineid = values.DisciplineID ?? null;
        Object.defineProperty(this, 'DisciplineID', {
            get(): number | null {
                return this.__disciplineid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DisciplineID', this.__disciplineid, value);
                this.__disciplineid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Distance = values.Distance ?? 0;
        this.__distance = values.Distance ?? 0;
        Object.defineProperty(this, 'Distance', {
            get(): number {
                return this.__distance;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Distance', this.__distance, value);
                this.__distance = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideFromLive = values.HideFromLive ?? false;
        this.__hidefromlive = values.HideFromLive ?? false;
        Object.defineProperty(this, 'HideFromLive', {
            get(): boolean {
                return this.__hidefromlive;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideFromLive', this.__hidefromlive, value);
                this.__hidefromlive = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IndividualStart = values.IndividualStart ?? false;
        this.__individualstart = values.IndividualStart ?? false;
        Object.defineProperty(this, 'IndividualStart', {
            get(): boolean {
                return this.__individualstart;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IndividualStart', this.__individualstart, value);
                this.__individualstart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Level = values.Level ?? 0;
        this.__level = values.Level ?? 0;
        Object.defineProperty(this, 'Level', {
            get(): number {
                return this.__level;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Level', this.__level, value);
                this.__level = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MapID = values.MapID ?? null;
        this.__mapid = values.MapID ?? null;
        Object.defineProperty(this, 'MapID', {
            get(): number | null {
                return this.__mapid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('MapID', this.__mapid, value);
                this.__mapid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MaxContestants = values.MaxContestants ?? null;
        this.__maxcontestants = values.MaxContestants ?? null;
        Object.defineProperty(this, 'MaxContestants', {
            get(): number | null {
                return this.__maxcontestants;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('MaxContestants', this.__maxcontestants, value);
                this.__maxcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? null;
        this.__name = values.Name ?? null;
        Object.defineProperty(this, 'Name', {
            get(): string | null {
                return this.__name;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Number = values.Number ?? 0;
        this.__number = values.Number ?? 0;
        Object.defineProperty(this, 'Number', {
            get(): number {
                return this.__number;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Number', this.__number, value);
                this.__number = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OverrideSpeed = values.OverrideSpeed ?? null;
        this.__overridespeed = values.OverrideSpeed ?? null;
        Object.defineProperty(this, 'OverrideSpeed', {
            get(): number | null {
                return this.__overridespeed;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('OverrideSpeed', this.__overridespeed, value);
                this.__overridespeed = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Pursuit = values.Pursuit ?? false;
        this.__pursuit = values.Pursuit ?? false;
        Object.defineProperty(this, 'Pursuit', {
            get(): boolean {
                return this.__pursuit;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Pursuit', this.__pursuit, value);
                this.__pursuit = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceTransferRules = values.RaceTransferRules ?? [];
        this.__racetransferrules = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RaceTransferRules ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceTransferRules', newVal),
        });
        this.__racetransferrules.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceTransferRules', this.RaceTransferRules),
            );
        });
        Object.defineProperty(this, 'RaceTransferRules', {
            get(): RaceTransferRule[] {
                return this.__racetransferrules;
            },
            set(value: RaceTransferRule[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__racetransferrules = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RaceTransferRule[], newVal: RaceTransferRule[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceTransferRules', newVal),
                });
                this.__racetransferrules.forEach((child: RaceTransferRule) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceTransferRules', this.RaceTransferRules),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceTransferRules', this.RaceTransferRules);
            },
            enumerable: true,
            configurable: true,
        });

        this.RankingResolution = values.RankingResolution ?? null;
        this.__rankingresolution = values.RankingResolution ?? null;
        Object.defineProperty(this, 'RankingResolution', {
            get(): number | null {
                return this.__rankingresolution;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RankingResolution', this.__rankingresolution, value);
                this.__rankingresolution = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayAllowTeamnameChange = values.RelayAllowTeamnameChange ?? false;
        this.__relayallowteamnamechange = values.RelayAllowTeamnameChange ?? false;
        Object.defineProperty(this, 'RelayAllowTeamnameChange', {
            get(): boolean {
                return this.__relayallowteamnamechange;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RelayAllowTeamnameChange', this.__relayallowteamnamechange, value);
                this.__relayallowteamnamechange = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayDuplicateParticipant = values.RelayDuplicateParticipant ?? false;
        this.__relayduplicateparticipant = values.RelayDuplicateParticipant ?? false;
        Object.defineProperty(this, 'RelayDuplicateParticipant', {
            get(): boolean {
                return this.__relayduplicateparticipant;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RelayDuplicateParticipant', this.__relayduplicateparticipant, value);
                this.__relayduplicateparticipant = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayGetParticipantInfo = values.RelayGetParticipantInfo ?? false;
        this.__relaygetparticipantinfo = values.RelayGetParticipantInfo ?? false;
        Object.defineProperty(this, 'RelayGetParticipantInfo', {
            get(): boolean {
                return this.__relaygetparticipantinfo;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RelayGetParticipantInfo', this.__relaygetparticipantinfo, value);
                this.__relaygetparticipantinfo = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayHideChip = values.RelayHideChip ?? false;
        this.__relayhidechip = values.RelayHideChip ?? false;
        Object.defineProperty(this, 'RelayHideChip', {
            get(): boolean {
                return this.__relayhidechip;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RelayHideChip', this.__relayhidechip, value);
                this.__relayhidechip = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayHideSearch = values.RelayHideSearch ?? false;
        this.__relayhidesearch = values.RelayHideSearch ?? false;
        Object.defineProperty(this, 'RelayHideSearch', {
            get(): boolean {
                return this.__relayhidesearch;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RelayHideSearch', this.__relayhidesearch, value);
                this.__relayhidesearch = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayTeamMaxContestants = values.RelayTeamMaxContestants ?? null;
        this.__relayteammaxcontestants = values.RelayTeamMaxContestants ?? null;
        Object.defineProperty(this, 'RelayTeamMaxContestants', {
            get(): number | null {
                return this.__relayteammaxcontestants;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RelayTeamMaxContestants', this.__relayteammaxcontestants, value);
                this.__relayteammaxcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayTeamMinContestants = values.RelayTeamMinContestants ?? null;
        this.__relayteammincontestants = values.RelayTeamMinContestants ?? null;
        Object.defineProperty(this, 'RelayTeamMinContestants', {
            get(): number | null {
                return this.__relayteammincontestants;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RelayTeamMinContestants', this.__relayteammincontestants, value);
                this.__relayteammincontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RequireActiveLicense = values.RequireActiveLicense ?? false;
        this.__requireactivelicense = values.RequireActiveLicense ?? false;
        Object.defineProperty(this, 'RequireActiveLicense', {
            get(): boolean {
                return this.__requireactivelicense;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RequireActiveLicense', this.__requireactivelicense, value);
                this.__requireactivelicense = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResultParent = values.ResultParent ?? null;
        this.__resultparent = values.ResultParent ?? null;
        Object.defineProperty(this, 'ResultParent', {
            get(): number | null {
                return this.__resultparent;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ResultParent', this.__resultparent, value);
                this.__resultparent = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResultSetup = values.ResultSetup ?? null;
        this.ResultSetup = values.ResultSetup ?? null;
        this.ResultSetup?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('ResultSetup', this.ResultSetup),
        );

        this.RoundType = values.RoundType ?? 'Down';
        this.__roundtype = values.RoundType ?? 'Down';
        Object.defineProperty(this, 'RoundType', {
            get(): RoundType | null {
                return this.__roundtype;
            },
            set(value: RoundType | null) {
                this.__entityStateHandler.propertyChanged('RoundType', this.__roundtype, value);
                this.__roundtype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Run = values.Run ?? 0;
        this.__run = values.Run ?? 0;
        Object.defineProperty(this, 'Run', {
            get(): number {
                return this.__run;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Run', this.__run, value);
                this.__run = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShortName = values.ShortName ?? null;
        this.__shortname = values.ShortName ?? null;
        Object.defineProperty(this, 'ShortName', {
            get(): string | null {
                return this.__shortname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ShortName', this.__shortname, value);
                this.__shortname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportsLevelID = values.SportsLevelID ?? null;
        this.__sportslevelid = values.SportsLevelID ?? null;
        Object.defineProperty(this, 'SportsLevelID', {
            get(): number | null {
                return this.__sportslevelid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SportsLevelID', this.__sportslevelid, value);
                this.__sportslevelid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackColorLive = values.TrackColorLive ?? null;
        this.__trackcolorlive = values.TrackColorLive ?? null;
        Object.defineProperty(this, 'TrackColorLive', {
            get(): string | null {
                return this.__trackcolorlive;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TrackColorLive', this.__trackcolorlive, value);
                this.__trackcolorlive = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.VirtualRaceExternalURL = values.VirtualRaceExternalURL ?? null;
        this.__virtualraceexternalurl = values.VirtualRaceExternalURL ?? null;
        Object.defineProperty(this, 'VirtualRaceExternalURL', {
            get(): string | null {
                return this.__virtualraceexternalurl;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('VirtualRaceExternalURL', this.__virtualraceexternalurl, value);
                this.__virtualraceexternalurl = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.VirtualRaceType = values.VirtualRaceType ?? 'None';
        this.__virtualracetype = values.VirtualRaceType ?? 'None';
        Object.defineProperty(this, 'VirtualRaceType', {
            get(): VirtualRaceType {
                return this.__virtualracetype;
            },
            set(value: VirtualRaceType) {
                this.__entityStateHandler.propertyChanged('VirtualRaceType', this.__virtualracetype, value);
                this.__virtualracetype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.VirtualTimeType = values.VirtualTimeType ?? null;
        this.__virtualtimetype = values.VirtualTimeType ?? null;
        Object.defineProperty(this, 'VirtualTimeType', {
            get(): number | null {
                return this.__virtualtimetype;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('VirtualTimeType', this.__virtualtimetype, value);
                this.__virtualtimetype = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CancellationDeadline": ${this.CancellationDeadline ? '"' + formatDateIso(this.CancellationDeadline) + '"' : null}` +
            ',' +
            `"Date": ${this.Date ? '"' + formatDateIso(this.Date) + '"' : null}` +
            ',' +
            `"DecimalPlacesInResult": ${JSON.stringify(this.DecimalPlacesInResult)}` +
            ',' +
            `"DigitsInBib": ${JSON.stringify(this.DigitsInBib)}` +
            ',' +
            `"DisciplineID": ${JSON.stringify(this.DisciplineID)}` +
            ',' +
            `"Distance": ${JSON.stringify(this.Distance)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"HideFromLive": ${JSON.stringify(this.HideFromLive)}` +
            ',' +
            `"IndividualStart": ${JSON.stringify(this.IndividualStart)}` +
            ',' +
            `"Level": ${JSON.stringify(this.Level)}` +
            ',' +
            `"MapID": ${JSON.stringify(this.MapID)}` +
            ',' +
            `"MaxContestants": ${JSON.stringify(this.MaxContestants)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"Number": ${JSON.stringify(this.Number)}` +
            ',' +
            `"OverrideSpeed": ${JSON.stringify(this.OverrideSpeed)}` +
            ',' +
            `"Pursuit": ${JSON.stringify(this.Pursuit)}` +
            ',' +
            `"RaceTransferRules": [ ${this.RaceTransferRules.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RankingResolution": ${JSON.stringify(this.RankingResolution)}` +
            ',' +
            `"RelayAllowTeamnameChange": ${JSON.stringify(this.RelayAllowTeamnameChange)}` +
            ',' +
            `"RelayDuplicateParticipant": ${JSON.stringify(this.RelayDuplicateParticipant)}` +
            ',' +
            `"RelayGetParticipantInfo": ${JSON.stringify(this.RelayGetParticipantInfo)}` +
            ',' +
            `"RelayHideChip": ${JSON.stringify(this.RelayHideChip)}` +
            ',' +
            `"RelayHideSearch": ${JSON.stringify(this.RelayHideSearch)}` +
            ',' +
            `"RelayTeamMaxContestants": ${JSON.stringify(this.RelayTeamMaxContestants)}` +
            ',' +
            `"RelayTeamMinContestants": ${JSON.stringify(this.RelayTeamMinContestants)}` +
            ',' +
            `"RequireActiveLicense": ${JSON.stringify(this.RequireActiveLicense)}` +
            ',' +
            `"ResultParent": ${JSON.stringify(this.ResultParent)}` +
            ',' +
            `"ResultSetup": ${!this.ResultSetup ? null : this.ResultSetup?.serialize()}` +
            ',' +
            `"RoundType": ${this.RoundType == null ? null : this.RoundType == 'Down' ? 0 : this.RoundType == 'Round' ? 1 : this.RoundType == 'Up' ? 2 : null}` +
            ',' +
            `"Run": ${JSON.stringify(this.Run)}` +
            ',' +
            `"ShortName": ${JSON.stringify(this.ShortName)}` +
            ',' +
            `"SportsLevelID": ${JSON.stringify(this.SportsLevelID)}` +
            ',' +
            `"TrackColorLive": ${JSON.stringify(this.TrackColorLive)}` +
            ',' +
            `"VirtualRaceExternalURL": ${JSON.stringify(this.VirtualRaceExternalURL)}` +
            ',' +
            `"VirtualRaceType": ${this.VirtualRaceType == 'None' ? 0 : this.VirtualRaceType == 'Section' ? 1 : this.VirtualRaceType == 'CupIndividualProlog' ? 2 : this.VirtualRaceType == 'CupSameLevel' ? 3 : this.VirtualRaceType == 'BestLap' ? 4 : this.VirtualRaceType == 'CupUci' ? 5 : this.VirtualRaceType == 'CupFis' ? 6 : this.VirtualRaceType == 'ShowUrl' ? 9 : this.VirtualRaceType == 'ShowTriggerControl' ? 10 : this.VirtualRaceType == 'CUPNSFUPDOWN' ? 11 : 0}` +
            ',' +
            `"VirtualTimeType": ${JSON.stringify(this.VirtualTimeType)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Race from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CancellationDeadline?: Date | null;
        Date?: Date | null;
        DecimalPlacesInResult?: number | null;
        DigitsInBib?: number | null;
        DisciplineID?: number | null;
        Distance?: number;
        EventID?: number;
        HideFromLive?: boolean;
        IndividualStart?: boolean;
        Level?: number;
        MapID?: number | null;
        MaxContestants?: number | null;
        Name?: string | null;
        Number?: number;
        OverrideSpeed?: number | null;
        Pursuit?: boolean;
        RaceTransferRules?: RaceTransferRule[];
        RankingResolution?: number | null;
        RelayAllowTeamnameChange?: boolean;
        RelayDuplicateParticipant?: boolean;
        RelayGetParticipantInfo?: boolean;
        RelayHideChip?: boolean;
        RelayHideSearch?: boolean;
        RelayTeamMaxContestants?: number | null;
        RelayTeamMinContestants?: number | null;
        RequireActiveLicense?: boolean;
        ResultParent?: number | null;
        ResultSetup?: ResultSetupRace | null;
        RoundType?: RoundType | null;
        Run?: number;
        ShortName?: string | null;
        SportsLevelID?: number | null;
        TrackColorLive?: string | null;
        VirtualRaceExternalURL?: string | null;
        VirtualRaceType?: VirtualRaceType;
        VirtualTimeType?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Race {
        return new Race(values);
    }

    public static _FromServerJson(json: any): Race {
        return new Race({
            ID: json.ID,
            CancellationDeadline: json.CancellationDeadline
                ? json.CancellationDeadline instanceof Date
                    ? json.CancellationDeadline
                    : new Date(json.CancellationDeadline)
                : null,
            Date: json.Date ? (json.Date instanceof Date ? json.Date : new Date(json.Date)) : null,
            DecimalPlacesInResult: json.DecimalPlacesInResult ?? null,
            DigitsInBib: json.DigitsInBib ?? null,
            DisciplineID: json.DisciplineID ?? null,
            Distance: json.Distance,
            EventID: json.EventID,
            HideFromLive: json.HideFromLive,
            IndividualStart: json.IndividualStart,
            Level: json.Level,
            MapID: json.MapID ?? null,
            MaxContestants: json.MaxContestants ?? null,
            Name: json.Name ?? '',
            Number: json.Number,
            OverrideSpeed: json.OverrideSpeed ?? null,
            Pursuit: json.Pursuit,
            RaceTransferRules: json.RaceTransferRules?.map((x: any) => RaceTransferRule._FromServerJson(x)),
            RankingResolution: json.RankingResolution ?? null,
            RelayAllowTeamnameChange: json.RelayAllowTeamnameChange,
            RelayDuplicateParticipant: json.RelayDuplicateParticipant,
            RelayGetParticipantInfo: json.RelayGetParticipantInfo,
            RelayHideChip: json.RelayHideChip,
            RelayHideSearch: json.RelayHideSearch,
            RelayTeamMaxContestants: json.RelayTeamMaxContestants ?? null,
            RelayTeamMinContestants: json.RelayTeamMinContestants ?? null,
            RequireActiveLicense: json.RequireActiveLicense,
            ResultParent: json.ResultParent ?? null,
            ResultSetup: json.ResultSetup ? ResultSetupRace._FromServerJson(json.ResultSetup) : null,
            RoundType:
                json.RoundType == null
                    ? null
                    : json.RoundType == 0
                      ? 'Down'
                      : json.RoundType == 1
                        ? 'Round'
                        : json.RoundType == 2
                          ? 'Up'
                          : null,
            Run: json.Run,
            ShortName: json.ShortName ?? '',
            SportsLevelID: json.SportsLevelID ?? null,
            TrackColorLive: json.TrackColorLive ?? '',
            VirtualRaceExternalURL: json.VirtualRaceExternalURL ?? '',
            VirtualRaceType:
                json.VirtualRaceType == 0
                    ? 'None'
                    : json.VirtualRaceType == 1
                      ? 'Section'
                      : json.VirtualRaceType == 2
                        ? 'CupIndividualProlog'
                        : json.VirtualRaceType == 3
                          ? 'CupSameLevel'
                          : json.VirtualRaceType == 4
                            ? 'BestLap'
                            : json.VirtualRaceType == 5
                              ? 'CupUci'
                              : json.VirtualRaceType == 6
                                ? 'CupFis'
                                : json.VirtualRaceType == 9
                                  ? 'ShowUrl'
                                  : json.VirtualRaceType == 10
                                    ? 'ShowTriggerControl'
                                    : json.VirtualRaceType == 11
                                      ? 'CUPNSFUPDOWN'
                                      : 'None',
            VirtualTimeType: json.VirtualTimeType ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Race from another instance of Race.
     * @param other The Race you want to clone
     * @returns A cloned Race
     */
    public static FromOther(other: Race): Race {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Race._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Race.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'CancellationDeadline'
            | 'Date'
            | 'DecimalPlacesInResult'
            | 'DigitsInBib'
            | 'DisciplineID'
            | 'Distance'
            | 'EventID'
            | 'HideFromLive'
            | 'IndividualStart'
            | 'Level'
            | 'MapID'
            | 'MaxContestants'
            | 'Name'
            | 'Number'
            | 'OverrideSpeed'
            | 'Pursuit'
            | 'RaceTransferRules'
            | 'RankingResolution'
            | 'RelayAllowTeamnameChange'
            | 'RelayDuplicateParticipant'
            | 'RelayGetParticipantInfo'
            | 'RelayHideChip'
            | 'RelayHideSearch'
            | 'RelayTeamMaxContestants'
            | 'RelayTeamMinContestants'
            | 'RequireActiveLicense'
            | 'ResultParent'
            | 'ResultSetup'
            | 'RoundType'
            | 'Run'
            | 'ShortName'
            | 'SportsLevelID'
            | 'TrackColorLive'
            | 'VirtualRaceExternalURL'
            | 'VirtualRaceType'
            | 'VirtualTimeType'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Race';
}
